/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext } from 'react'

// @mui core components
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'

// Settings page components
import FormField from 'components/FormField'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

function PayStubDetails() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your pay stub details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  return (
    <Card
      sx={{
        textAlign: 'left',
        boxShadow: 0,
        border: '1px solid #ddd',
        borderRadius: 2,
      }}
    >
      <Box px={3} pt={3} display="flex" justifyContent="space-between">
        <Typography variant="h3" fontSize={{ xs: '24px', md: '30px' }}>
          {intakeState.agency === 'u.s._postal_service_(usps)'
            ? 'Pay Stub'
            : 'Leave & Earnings'}
        </Typography>

        <Button
          variant="gradient"
          color="dark"
          sx={{ height: '100%' }}
          onClick={onSubmit}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
      </Box>
      <Box component="form" pb={3} px={3}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'baseSalary',
              label: (
                <>
                  <span>
                    Adjusted Base Annual Salary
                    <span style={{ color: 'red' }}> *</span>
                    <Tooltip
                      TransitionComponent={Fade}
                      title="Your Adjusted base salary is your base salary including any locality pay you receive."
                      placement="top"
                      color="info"
                    >
                      <IconButton size="small" color="dark">
                        <HelpIcon sx={{ marginTop: '-6px' }} />
                      </IconButton>
                    </Tooltip>
                  </span>
                </>
              ),
              placeholder: '$62,000.00',
              inputType: 'currencyFloat',
              value: intakeState.baseSalary || 0,
              onChange: updateIntakeStateAndSubmit((baseSalary) =>
                updateIntakeStateAtKey({ baseSalary })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'highThree',
              label: (
                <>
                  <span>
                    High Three
                    <span style={{ color: 'red' }}> *</span>
                    <Tooltip
                      TransitionComponent={Fade}
                      title="Highest three consecutive years of service. Used to calculate your pension"
                      placement="top"
                      color="info"
                    >
                      <IconButton size="small" color="dark">
                        <HelpIcon sx={{ marginTop: '-6px' }} />
                      </IconButton>
                    </Tooltip>
                  </span>
                </>
              ),
              placeholder: '$62,000.00',
              inputType: 'currencyFloat',
              value: intakeState.highThree,
              onChange: updateIntakeStateAndSubmit((highThree) =>
                updateIntakeStateAtKey({ highThree })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              id: 'netTakeHome',
              label: (
                <>
                  <span>
                    Net take home per pay period
                    <span style={{ color: 'red' }}> *</span>
                    <Tooltip
                      TransitionComponent={Fade}
                      title="How much you take home in a normal pay period net of all deductions"
                      placement="top"
                      color="info"
                    >
                      <IconButton size="small" color="dark">
                        <HelpIcon sx={{ marginTop: '-6px' }} />
                      </IconButton>
                    </Tooltip>
                  </span>
                </>
              ),
              placeholder: '$3,500',
              inputType: 'currencyFloat',
              value: intakeState.netTakeHome,
              onChange: updateIntakeStateAndSubmit((netTakeHome) =>
                updateIntakeStateAtKey({ netTakeHome })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'currentLeaveBalance',
              label: 'Current Earned Annual Leave Balance',
              placeholder: '100.0',
              inputType: 'floatField',
              value: intakeState.currentLeaveBalance,
              onChange: updateIntakeStateAndSubmit((currentLeaveBalance) =>
                updateIntakeStateAtKey({ currentLeaveBalance })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'currentSickLeaveBalance',
              label: 'Current Sick Leave Balance',
              placeholder: '100.0',
              inputType: 'floatField',
              value: intakeState.currentSickLeaveBalance,
              onChange: updateIntakeStateAndSubmit((currentSickLeaveBalance) =>
                updateIntakeStateAtKey({ currentSickLeaveBalance })
              ),
            })}
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

export default PayStubDetails
